import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../utils/api';
import usePolling from '../hooks/usePolling';
import './TreasuryPage.css';
import {
  Wallet, TrendingUp, TrendingDown, Clock, AlertTriangle,
  Search, Download, Filter, X, RefreshCw,
  ChevronRight, Lock, Unlock, Plus, ArrowDownLeft, ArrowUpRight,
  Ban, ChevronDown, ChevronUp, FileText, Shield, Sliders, Send
} from 'lucide-react';

// ============================================================================
// FORMATTERS
// ============================================================================
const fmtNGN = (n) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2
  }).format(parseFloat(n || 0));

const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' });
};

const maskAccount = (n) => {
  if (!n) return '';
  const s = String(n);
  return `${s.slice(0, 2)}••${s.slice(-3)}`;
};

const TX_CATEGORIES = [
  'SALE_HOLD', 'SERVICE_CHARGE', 'WITHDRAWAL', 'CHARGEBACK',
  'SUBSCRIPTION_CHARGE', 'POS_FEE_ACCRUAL', 'FUNDING',
  'CHOWDECK_DELIVERY', 'REFUND', 'ADJUSTMENT', 'REVERSAL', 'PROMOTIONAL_CREDIT'
];
const TX_SOURCES = [
  'PAYSTACK_CHECKOUT', 'FLUTTERWAVE_CHECKOUT', 'DVA',
  'POS', 'SUBSCRIPTION', 'WITHDRAWAL', 'CHARGEBACK', 'ADMIN', 'CHOWDECK'
];
const TX_STATUSES = ['PENDING', 'ON_HOLD', 'COMPLETED', 'FAILED', 'REVERSED'];
const WD_STATUSES = ['PENDING', 'PROCESSING', 'QUEUED', 'COMPLETED', 'FAILED', 'REVERSED'];
const CB_STATUSES = ['PENDING', 'WON', 'LOST', 'RESOLVED'];

// ============================================================================
// TREASURY PAGE
// ============================================================================
const TreasuryPage = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ledger');
  const [selectedMerchantId, setSelectedMerchantId] = useState(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await api.get('/admin/treasury/stats');
      setStats(r.stats);
    } catch (e) {
      console.error('[Treasury] stats error:', e);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="tr-page">
      <div className="tr-page-header">
        <div>
          <h1>Treasury</h1>
          <p>Wallet activity, withdrawals, chargebacks, and platform settings.</p>
        </div>
        <button className="tr-refresh" onClick={loadStats} title="Refresh stats">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <KpiGrid stats={stats} loading={statsLoading} />

      <div className="tr-tabs">
        {[
          { id: 'ledger', label: 'Ledger', Icon: FileText },
          { id: 'withdrawals', label: 'Withdrawals', Icon: ArrowUpRight },
          { id: 'chargebacks', label: 'Chargebacks', Icon: Shield },
          { id: 'settings', label: 'Settings', Icon: Sliders }
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`tr-tab ${activeTab === id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={15} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="tr-tab-body">
        {activeTab === 'ledger' && (
          <LedgerTab onSelectMerchant={setSelectedMerchantId} />
        )}
        {activeTab === 'withdrawals' && (
          <WithdrawalsTab onSelectMerchant={setSelectedMerchantId} onChange={loadStats} />
        )}
        {activeTab === 'chargebacks' && (
          <ChargebacksTab onSelectMerchant={setSelectedMerchantId} onChange={loadStats} />
        )}
        {activeTab === 'settings' && <SettingsTab />}
      </div>

      {selectedMerchantId && (
        <MerchantPanel
          merchantId={selectedMerchantId}
          onClose={() => setSelectedMerchantId(null)}
          onChange={() => { loadStats(); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// KPI GRID
// ============================================================================
const KpiGrid = ({ stats, loading }) => {
  const cards = [
    {
      label: 'Platform float',
      value: fmtNGN(stats?.platformFloat),
      sub: `${stats?.totalWallets ?? 0} wallets`,
      Icon: Wallet,
      tone: 'primary'
    },
    {
      label: "Today's inflow",
      value: fmtNGN(stats?.todayInflow),
      sub: 'Customer payments',
      Icon: TrendingUp,
      tone: 'positive'
    },
    {
      label: "Today's outflow",
      value: fmtNGN(stats?.todayOutflow),
      sub: 'Withdrawals + debits',
      Icon: TrendingDown,
      tone: 'neutral'
    },
    {
      label: 'Pending withdrawals',
      value: stats?.pendingWithdrawals ?? 0,
      sub: 'Awaiting transfer',
      Icon: Clock,
      tone: 'warning'
    },
    {
      label: 'Open chargebacks',
      value: stats?.pendingChargebacks ?? 0,
      sub: stats?.frozenWallets ? `${stats.frozenWallets} frozen` : 'None frozen',
      Icon: AlertTriangle,
      tone: stats?.pendingChargebacks ? 'danger' : 'neutral'
    }
  ];

  return (
    <div className="tr-kpi-grid">
      {cards.map(({ label, value, sub, Icon, tone }) => (
        <div key={label} className={`tr-kpi tr-kpi-${tone}`}>
          <div className="tr-kpi-icon"><Icon size={18} /></div>
          <div className="tr-kpi-body">
            <div className="tr-kpi-label">{label}</div>
            <div className="tr-kpi-value">{loading ? '—' : value}</div>
            <div className="tr-kpi-sub">{loading ? ' ' : sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// LEDGER TAB
// ============================================================================
const LedgerTab = ({ onSelectMerchant }) => {
  const [filters, setFilters] = useState({
    merchantSearch: '',
    search: '',
    type: '',
    category: '',
    source: '',
    status: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: ''
  });
  const [data, setData] = useState({ items: [], pagination: null, filteredSum: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('page', String(page));
    params.set('limit', '50');
    return params.toString();
  }, [filters, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/treasury/ledger?${queryString}`);
      setData({ items: r.items, pagination: r.pagination, filteredSum: r.filteredSum });
    } catch (e) {
      console.error('[Ledger] load error:', e);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set('format', 'csv');
    const base = process.env.REACT_APP_API_URL || '';
    const url = `${base ? base.replace(/\/$/, '') + (base.endsWith('/api') ? '' : '/api') : '/api'}/admin/treasury/ledger/export?${params.toString()}`;
    window.open(url, '_blank');
  }, [filters]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearFilters = () => {
    setFilters({
      merchantSearch: '', search: '', type: '', category: '', source: '',
      status: '', startDate: '', endDate: '', minAmount: '', maxAmount: ''
    });
    setPage(1);
  };

  return (
    <div className="tr-ledger">
      <div className="tr-toolbar">
        <div className="tr-toolbar-left">
          <div className="tr-search">
            <Search size={15} />
            <input
              placeholder="Search ref, order, reference"
              value={filters.search}
              onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
            />
          </div>
          <button
            className={`tr-filter-btn ${activeFilterCount ? 'has-active' : ''}`}
            onClick={() => setShowFilters(s => !s)}
          >
            <Filter size={14} />
            Filters
            {activeFilterCount > 0 && <span className="tr-filter-count">{activeFilterCount}</span>}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        <div className="tr-toolbar-right">
          <span className="tr-sum">Sum: <strong>{fmtNGN(data.filteredSum)}</strong></span>
          <button className="tr-icon-btn" onClick={downloadCsv} title="Download CSV">
            <Download size={15} />
            <span>CSV</span>
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="tr-filter-panel">
          <FilterField label="Merchant">
            <input
              placeholder="email or username"
              value={filters.merchantSearch}
              onChange={(e) => { setFilters(f => ({ ...f, merchantSearch: e.target.value })); setPage(1); }}
            />
          </FilterField>
          <FilterField label="Type">
            <select value={filters.type} onChange={(e) => { setFilters(f => ({ ...f, type: e.target.value })); setPage(1); }}>
              <option value="">All</option>
              <option value="CREDIT">Credit</option>
              <option value="DEBIT">Debit</option>
            </select>
          </FilterField>
          <FilterField label="Category">
            <select value={filters.category} onChange={(e) => { setFilters(f => ({ ...f, category: e.target.value })); setPage(1); }}>
              <option value="">All</option>
              {TX_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </FilterField>
          <FilterField label="Source">
            <select value={filters.source} onChange={(e) => { setFilters(f => ({ ...f, source: e.target.value })); setPage(1); }}>
              <option value="">All</option>
              {TX_SOURCES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </FilterField>
          <FilterField label="Status">
            <select value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
              <option value="">All</option>
              {TX_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </FilterField>
          <FilterField label="From">
            <input type="date" value={filters.startDate} onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }} />
          </FilterField>
          <FilterField label="To">
            <input type="date" value={filters.endDate} onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }} />
          </FilterField>
          <FilterField label="Min ₦">
            <input type="number" min="0" placeholder="0" value={filters.minAmount} onChange={(e) => { setFilters(f => ({ ...f, minAmount: e.target.value })); setPage(1); }} />
          </FilterField>
          <FilterField label="Max ₦">
            <input type="number" min="0" placeholder="∞" value={filters.maxAmount} onChange={(e) => { setFilters(f => ({ ...f, maxAmount: e.target.value })); setPage(1); }} />
          </FilterField>
          {activeFilterCount > 0 && (
            <button className="tr-link-btn" onClick={clearFilters}>
              <X size={14} /> Clear all
            </button>
          )}
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="tr-active-pills">
          {Object.entries(filters).map(([k, v]) => v ? (
            <button
              key={k}
              className="tr-pill"
              onClick={() => { setFilters(f => ({ ...f, [k]: '' })); setPage(1); }}
              title="Remove filter"
            >
              {k}: {String(v).slice(0, 20)} <X size={11} />
            </button>
          ) : null)}
        </div>
      )}

      <div className="tr-table-wrap">
        <table className="tr-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Merchant</th>
              <th>Type</th>
              <th>Category</th>
              <th>Source</th>
              <th>Amount</th>
              <th>Balance after</th>
              <th>Status</th>
              <th>Order</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="tr-empty">Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="tr-empty">No transactions match your filters.</td></tr>
            )}
            {!loading && data.items.map(tx => (
              <tr key={tx.id} className="tr-row" onClick={() => onSelectMerchant(tx.merchantId)}>
                <td className="tr-mono">{fmtDateTime(tx.createdAt)}</td>
                <td>
                  <div className="tr-merchant-cell">
                    <div className="tr-merchant-name">{tx.merchant?.store?.name || tx.merchant?.username || '—'}</div>
                    <div className="tr-merchant-email">{tx.merchant?.email || '—'}</div>
                  </div>
                </td>
                <td><TypeBadge type={tx.type} /></td>
                <td><span className="tr-category">{tx.category.replace(/_/g, ' ')}</span></td>
                <td className="tr-mono-sm">{tx.source || '—'}</td>
                <td className={`tr-amount ${tx.type === 'DEBIT' ? 'is-debit' : 'is-credit'}`}>
                  {tx.type === 'DEBIT' ? '−' : '+'}{fmtNGN(tx.amount)}
                </td>
                <td className="tr-mono">{fmtNGN(tx.balanceAfter)}</td>
                <td><StatusBadge status={tx.status} /></td>
                <td className="tr-mono-sm">{tx.orderNumber || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Pager pagination={data.pagination} onChange={setPage} />
      )}
    </div>
  );
};

// ============================================================================
// WITHDRAWALS TAB
// ============================================================================
const WithdrawalsTab = ({ onSelectMerchant, onChange }) => {
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [data, setData] = useState({ items: [], pagination: null, filteredSum: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [syncingId, setSyncingId] = useState(null); // withdrawal.id being synced
  const [syncResult, setSyncResult] = useState(null); // { ref, message, kind: 'success' | 'pending' | 'error' }
  const [dispatchingId, setDispatchingId] = useState(null); // withdrawal.id being dispatched
  const [retryingAll, setRetryingAll] = useState(false);
  const [floatStatus, setFloatStatus] = useState(null); // { paystackBalance, queuedCount, queuedAmount, deficit, paystackError }

  const loadFloatStatus = useCallback(async () => {
    try {
      const r = await api.get('/admin/treasury/float-status');
      setFloatStatus(r);
    } catch (e) {
      console.error('[Withdrawals] float-status error:', e);
    }
  }, []);

  // Float status poll — visibility-gated. Was firing every 60s even when
  // the Treasury tab was hidden or the admin had switched away.
  usePolling(loadFloatStatus, 60_000, [loadFloatStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', String(page));
      params.set('limit', '50');
      const r = await api.get(`/admin/treasury/withdrawals?${params.toString()}`);
      setData({ items: r.items, pagination: r.pagination, filteredSum: r.filteredSum });
    } catch (e) {
      console.error('[Withdrawals] load error:', e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const handleReverse = async () => {
    if (!reverseTarget) return;
    if (!reverseReason || reverseReason.trim().length < 3) {
      alert('Please enter a reason (min 3 chars).');
      return;
    }
    try {
      await api.post(`/admin/treasury/withdrawals/${reverseTarget.id}/reverse`, { reason: reverseReason.trim() });
      setReverseTarget(null);
      setReverseReason('');
      await load();
      onChange?.();
    } catch (e) {
      alert(`Reverse failed: ${e.message}`);
    }
  };

  const handleSync = async (w) => {
    setSyncingId(w.id);
    setSyncResult(null);
    try {
      const r = await api.post(`/admin/treasury/withdrawals/${w.id}/sync`, {});
      if (r.synced) {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: r.status === 'COMPLETED' ? 'success' : 'error',
          message: r.status === 'COMPLETED'
            ? `Paystack confirmed success — marked COMPLETED. Merchant has been notified.`
            : `Paystack reported "${r.paystackStatus}" — marked FAILED. Reservation returned to available balance.`
        });
        await load();
        await loadFloatStatus();
        onChange?.();
      } else if (r.reason === 'paystack-still-pending') {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: 'pending',
          message: `Paystack still reports "${r.paystackStatus}". No state change — try again in a few minutes (or wait for the 10-min auto-reconcile cron).`
        });
      } else {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: 'error',
          message: r.message || `Could not sync (${r.reason || 'unknown'}).`
        });
      }
    } catch (e) {
      setSyncResult({
        ref: w.withdrawalRef,
        kind: 'error',
        message: `Sync failed: ${e?.response?.data?.error || e.message}`
      });
    } finally {
      setSyncingId(null);
    }
  };

  const handleDispatch = async (w) => {
    setDispatchingId(w.id);
    setSyncResult(null);
    try {
      const r = await api.post(`/admin/treasury/withdrawals/${w.id}/dispatch`, {});
      if (r.dispatched) {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: 'success',
          message: `Dispatched to Paystack — marked PROCESSING. Merchant has been notified.`
        });
        await load();
        await loadFloatStatus();
        onChange?.();
      } else if (r.reason === 'still-low-float') {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: 'pending',
          message: r.message || `Paystack float still below this withdrawal — kept queued.`
        });
        await loadFloatStatus();
      } else {
        setSyncResult({
          ref: w.withdrawalRef,
          kind: 'error',
          message: r.message || `Could not dispatch (${r.reason || 'unknown'}).`
        });
      }
    } catch (e) {
      setSyncResult({
        ref: w.withdrawalRef,
        kind: 'error',
        message: `Dispatch failed: ${e?.response?.data?.error || e.message}`
      });
    } finally {
      setDispatchingId(null);
    }
  };

  const handleRetryAllQueued = async () => {
    if (retryingAll) return;
    setRetryingAll(true);
    setSyncResult(null);
    try {
      const r = await api.post('/admin/treasury/withdrawals/retry-queued', {});
      const kind = r.dispatched > 0 ? 'success' : (r.errors > 0 ? 'error' : 'pending');
      setSyncResult({
        ref: 'Bulk retry',
        kind,
        message:
          r.swept === 0
            ? 'No queued withdrawals to retry.'
            : `Swept ${r.swept} — dispatched ${r.dispatched}, still queued ${r.stillQueued}, errors ${r.errors}.`
      });
      await load();
      await loadFloatStatus();
      onChange?.();
    } catch (e) {
      setSyncResult({
        ref: 'Bulk retry',
        kind: 'error',
        message: `Retry failed: ${e?.response?.data?.error || e.message}`
      });
    } finally {
      setRetryingAll(false);
    }
  };

  return (
    <div className="tr-withdrawals">
      {floatStatus && (() => {
        const hasError = !!floatStatus.paystackError;
        const lowFloat = !hasError && floatStatus.deficit > 0;
        const queuedExists = floatStatus.queuedCount > 0;
        const tone = hasError ? 'error' : (lowFloat || queuedExists ? 'warn' : 'ok');
        return (
          <div className={`tr-float-banner tr-float-banner-${tone}`}>
            <div className="tr-float-banner-row">
              <span className="tr-float-label">Platform Paystack balance</span>
              <strong className="tr-float-value">
                {hasError ? '—' : fmtNGN(floatStatus.paystackBalance || 0)}
              </strong>
            </div>
            <div className="tr-float-banner-row">
              <span className="tr-float-label">Queued withdrawals</span>
              <strong className="tr-float-value">
                {floatStatus.queuedCount} {floatStatus.queuedCount === 1 ? 'request' : 'requests'}
                {queuedExists ? ` · ${fmtNGN(floatStatus.queuedAmount)}` : ''}
              </strong>
            </div>
            {lowFloat && (
              <div className="tr-float-banner-msg">
                Float short by <strong>{fmtNGN(floatStatus.deficit)}</strong> — top up the Paystack balance to release queued payouts.
              </div>
            )}
            {hasError && (
              <div className="tr-float-banner-msg">
                Could not read Paystack balance: {floatStatus.paystackError}
              </div>
            )}
            {queuedExists && (
              <div className="tr-float-banner-actions">
                <button
                  className="tr-float-banner-btn"
                  onClick={handleRetryAllQueued}
                  disabled={retryingAll}
                  title="Sweep every QUEUED withdrawal and try to dispatch it to Paystack now"
                >
                  <RefreshCw size={13} className={retryingAll ? 'tr-spin' : ''} />
                  {retryingAll ? 'Retrying…' : 'Retry all queued'}
                </button>
              </div>
            )}
          </div>
        );
      })()}
      {syncResult && (
        <div className={`tr-sync-banner tr-sync-banner-${syncResult.kind}`}>
          <div className="tr-sync-banner-content">
            <strong>{syncResult.ref}</strong>
            <span>{syncResult.message}</span>
          </div>
          <button className="tr-sync-banner-dismiss" onClick={() => setSyncResult(null)} aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="tr-toolbar">
        <div className="tr-toolbar-left tr-toolbar-filter-row">
          <select value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All statuses</option>
            {WD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="date" value={filters.startDate} onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }} />
          <input type="date" value={filters.endDate} onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }} />
        </div>
        <div className="tr-toolbar-right">
          <span className="tr-sum">Sum: <strong>{fmtNGN(data.filteredSum)}</strong></span>
        </div>
      </div>

      <div className="tr-table-wrap">
        <table className="tr-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Merchant</th>
              <th>Amount</th>
              <th>Bank</th>
              <th>Status</th>
              <th>Reference</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="tr-empty">Loading…</td></tr>)}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="tr-empty">No withdrawals.</td></tr>
            )}
            {!loading && data.items.map(w => (
              <tr key={w.id} className="tr-row">
                <td className="tr-mono" onClick={() => onSelectMerchant(w.merchantId)}>{fmtDateTime(w.initiatedAt)}</td>
                <td onClick={() => onSelectMerchant(w.merchantId)}>
                  <div className="tr-merchant-cell">
                    <div className="tr-merchant-name">{w.merchant?.store?.name || '—'}</div>
                    <div className="tr-merchant-email">{w.merchant?.email}</div>
                  </div>
                </td>
                <td className="tr-amount is-debit">{fmtNGN(w.amount)}</td>
                <td>
                  <div className="tr-bank-cell">
                    <div>{w.bankName}</div>
                    <div className="tr-mono-sm">{maskAccount(w.accountNumber)}</div>
                  </div>
                </td>
                <td><StatusBadge status={w.status} /></td>
                <td className="tr-mono-sm">{w.withdrawalRef}</td>
                <td>
                  <div className="tr-row-actions">
                    {w.status === 'PROCESSING' && (
                      <button
                        className="tr-row-action"
                        onClick={() => handleSync(w)}
                        disabled={syncingId === w.id}
                        title="Query Paystack for the latest status and apply it"
                      >
                        <RefreshCw size={13} className={syncingId === w.id ? 'tr-spin' : ''} />
                        {syncingId === w.id ? 'Syncing…' : 'Sync'}
                      </button>
                    )}
                    {w.status === 'QUEUED' && (
                      <button
                        className="tr-row-action"
                        onClick={() => handleDispatch(w)}
                        disabled={dispatchingId === w.id}
                        title="Try to dispatch this withdrawal to Paystack now (skipped if platform float is still too low)"
                      >
                        <Send size={13} className={dispatchingId === w.id ? 'tr-spin' : ''} />
                        {dispatchingId === w.id ? 'Dispatching…' : 'Dispatch now'}
                      </button>
                    )}
                    {['PENDING', 'PROCESSING', 'QUEUED'].includes(w.status) && (
                      <button className="tr-row-action tr-row-action-danger" onClick={() => setReverseTarget(w)}>
                        <Ban size={13} /> Reverse
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Pager pagination={data.pagination} onChange={setPage} />
      )}

      {reverseTarget && (
        <Modal title={`Force-reverse withdrawal ${reverseTarget.withdrawalRef}`} onClose={() => { setReverseTarget(null); setReverseReason(''); }}>
          <p className="tr-modal-note">
            This returns <strong>{fmtNGN(reverseTarget.amount)}</strong> to the merchant's available balance and marks the withdrawal as REVERSED.
            Use only when Paystack will not settle this transfer.
          </p>
          <label className="tr-field">
            <span>Reason (required, min 3 chars)</span>
            <textarea rows={3} value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
          </label>
          <div className="tr-modal-actions">
            <button className="tr-btn-secondary" onClick={() => { setReverseTarget(null); setReverseReason(''); }}>Cancel</button>
            <button className="tr-btn-danger" onClick={handleReverse}>Reverse withdrawal</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============================================================================
// CHARGEBACKS TAB
// ============================================================================
const ChargebacksTab = ({ onSelectMerchant, onChange }) => {
  const [filters, setFilters] = useState({ status: 'PENDING', startDate: '', endDate: '' });
  const [data, setData] = useState({ items: [], pagination: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolution, setResolution] = useState('WON');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', String(page));
      params.set('limit', '50');
      const r = await api.get(`/admin/treasury/chargebacks?${params.toString()}`);
      setData({ items: r.items, pagination: r.pagination });
    } catch (e) {
      console.error('[Chargebacks] load error:', e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async () => {
    if (!resolveTarget) return;
    try {
      await api.post(`/admin/treasury/chargebacks/${resolveTarget.id}/resolve`, {
        resolution,
        notes: resolutionNotes.trim() || null
      });
      setResolveTarget(null);
      setResolution('WON');
      setResolutionNotes('');
      await load();
      onChange?.();
    } catch (e) {
      alert(`Resolve failed: ${e.message}`);
    }
  };

  return (
    <div className="tr-chargebacks">
      <div className="tr-toolbar">
        <div className="tr-toolbar-left tr-toolbar-filter-row">
          <select value={filters.status} onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All statuses</option>
            {CB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="date" value={filters.startDate} onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }} />
          <input type="date" value={filters.endDate} onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }} />
        </div>
      </div>

      <div className="tr-table-wrap">
        <table className="tr-table">
          <thead>
            <tr>
              <th>Opened</th>
              <th>Merchant</th>
              <th>Amount</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Ref</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="tr-empty">Loading…</td></tr>)}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="tr-empty">No chargebacks match.</td></tr>
            )}
            {!loading && data.items.map(c => (
              <tr key={c.id} className="tr-row">
                <td className="tr-mono" onClick={() => onSelectMerchant(c.merchantId)}>{fmtDateTime(c.createdAt)}</td>
                <td onClick={() => onSelectMerchant(c.merchantId)}>
                  <div className="tr-merchant-cell">
                    <div className="tr-merchant-name">{c.merchant?.store?.name || '—'}</div>
                    <div className="tr-merchant-email">{c.merchant?.email}</div>
                  </div>
                </td>
                <td className="tr-amount is-debit">{fmtNGN(c.amount)}</td>
                <td className="tr-truncate">{c.reason || '—'}</td>
                <td><StatusBadge status={c.status} /></td>
                <td className="tr-mono-sm">{c.chargebackRef}</td>
                <td>
                  {c.status === 'PENDING' && (
                    <button className="tr-row-action" onClick={() => setResolveTarget(c)}>
                      Resolve <ChevronRight size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Pager pagination={data.pagination} onChange={setPage} />
      )}

      {resolveTarget && (
        <Modal title={`Resolve chargeback ${resolveTarget.chargebackRef}`} onClose={() => { setResolveTarget(null); setResolutionNotes(''); }}>
          <p className="tr-modal-note">
            <strong>{fmtNGN(resolveTarget.amount)}</strong> — {resolveTarget.merchant?.email}
          </p>
          <label className="tr-field">
            <span>Resolution</span>
            <div className="tr-radio-group">
              {[
                { v: 'WON', label: 'Won — no debit, unfreeze wallet' },
                { v: 'LOST', label: 'Lost — debit wallet, unfreeze (if last open)' },
                { v: 'RESOLVED', label: 'Resolved — no debit, unfreeze' }
              ].map(({ v, label }) => (
                <label key={v} className={`tr-radio ${resolution === v ? 'is-active' : ''}`}>
                  <input type="radio" name="resolution" value={v} checked={resolution === v} onChange={(e) => setResolution(e.target.value)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </label>
          <label className="tr-field">
            <span>Notes (optional)</span>
            <textarea rows={3} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
          </label>
          <div className="tr-modal-actions">
            <button className="tr-btn-secondary" onClick={() => { setResolveTarget(null); setResolutionNotes(''); }}>Cancel</button>
            <button className={`tr-btn-${resolution === 'LOST' ? 'danger' : 'primary'}`} onClick={handleResolve}>Resolve</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============================================================================
// SETTINGS TAB
// ============================================================================
const SettingsTab = () => {
  const [config, setConfig] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingTier, setSavingTier] = useState(null);
  const [draftConfig, setDraftConfig] = useState({});
  const [draftTierOverdraft, setDraftTierOverdraft] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/treasury/settings');
      setConfig(r.config);
      setTiers(r.tiers);
      setDraftConfig({
        minimumWithdrawal: r.config?.minimumWithdrawal ?? 2000,
        backstopHoldDays: r.config?.backstopHoldDays ?? 14,
        holdPeriodHours: r.config?.holdPeriodHours ?? 48,
        releaseCondition: r.config?.releaseCondition ?? 'TIME_OR_DELIVERED',
        holdEnabled: r.config?.holdEnabled ?? true,
        autoReleaseEnabled: r.config?.autoReleaseEnabled ?? true,
        autoApprovalLimit: r.config?.autoApprovalLimit ?? 50000
      });
      setDraftTierOverdraft(Object.fromEntries(r.tiers.map(t => [t.tier, t.overdraftLimit])));
    } catch (e) {
      console.error('[Settings] load error:', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/treasury/settings', draftConfig);
      setConfig(r.config);
      alert('Wallet settings saved.');
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const saveTierOverdraft = async (tier) => {
    setSavingTier(tier);
    try {
      await api.put(`/admin/treasury/tiers/${encodeURIComponent(tier)}/overdraft`, {
        overdraftLimit: draftTierOverdraft[tier]
      });
      await load();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setSavingTier(null);
    }
  };

  if (!config) return <div className="tr-empty">Loading…</div>;

  return (
    <div className="tr-settings">
      <div className="tr-settings-card">
        <h3>Wallet rules</h3>
        <div className="tr-settings-grid">
          <label className="tr-field">
            <span>Minimum withdrawal (₦)</span>
            <input type="number" min="0" value={draftConfig.minimumWithdrawal}
              onChange={(e) => setDraftConfig(d => ({ ...d, minimumWithdrawal: parseFloat(e.target.value) || 0 }))} />
          </label>
          <label className="tr-field">
            <span>Backstop hold days</span>
            <input type="number" min="1" value={draftConfig.backstopHoldDays}
              onChange={(e) => setDraftConfig(d => ({ ...d, backstopHoldDays: parseInt(e.target.value, 10) || 1 }))} />
          </label>
          <label className="tr-field">
            <span>Hold period hours (legacy)</span>
            <input type="number" min="0" value={draftConfig.holdPeriodHours}
              onChange={(e) => setDraftConfig(d => ({ ...d, holdPeriodHours: parseInt(e.target.value, 10) || 0 }))} />
          </label>
          <label className="tr-field">
            <span>Release condition</span>
            <select value={draftConfig.releaseCondition} onChange={(e) => setDraftConfig(d => ({ ...d, releaseCondition: e.target.value }))}>
              <option value="TIME_BASED">Time only</option>
              <option value="ORDER_DELIVERED">Delivered only</option>
              <option value="TIME_OR_DELIVERED">Time or delivered</option>
            </select>
          </label>
          <label className="tr-field">
            <span>Auto-approval limit (₦)</span>
            <input type="number" min="0" value={draftConfig.autoApprovalLimit}
              onChange={(e) => setDraftConfig(d => ({ ...d, autoApprovalLimit: parseFloat(e.target.value) || 0 }))} />
          </label>
          <label className="tr-toggle-field">
            <input type="checkbox" checked={draftConfig.holdEnabled}
              onChange={(e) => setDraftConfig(d => ({ ...d, holdEnabled: e.target.checked }))} />
            <span>Hold enabled</span>
          </label>
          <label className="tr-toggle-field">
            <input type="checkbox" checked={draftConfig.autoReleaseEnabled}
              onChange={(e) => setDraftConfig(d => ({ ...d, autoReleaseEnabled: e.target.checked }))} />
            <span>Auto-release on schedule</span>
          </label>
        </div>
        <div className="tr-settings-actions">
          <button className="tr-btn-primary" disabled={saving} onClick={saveConfig}>
            {saving ? 'Saving…' : 'Save wallet rules'}
          </button>
        </div>
      </div>

      <div className="tr-settings-card">
        <h3>Tier overdraft limits</h3>
        <p className="tr-settings-note">Per-tier negative-balance cap. Per-merchant override via the merchant panel.</p>
        <div className="tr-tier-list">
          {tiers.map(t => (
            <div key={t.tier} className="tr-tier-row">
              <div className="tr-tier-name">
                <strong>{t.name}</strong>
                <span className="tr-tier-code">{t.tier}</span>
              </div>
              <div className="tr-tier-input">
                <span className="tr-currency-prefix">₦</span>
                <input type="number" min="0" value={draftTierOverdraft[t.tier] ?? 0}
                  onChange={(e) => setDraftTierOverdraft(d => ({ ...d, [t.tier]: parseFloat(e.target.value) || 0 }))} />
              </div>
              <button
                className="tr-btn-secondary tr-btn-sm"
                disabled={savingTier === t.tier || draftTierOverdraft[t.tier] === t.overdraftLimit}
                onClick={() => saveTierOverdraft(t.tier)}>
                {savingTier === t.tier ? 'Saving…' : 'Save'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MERCHANT SIDE PANEL
// ============================================================================
const MerchantPanel = ({ merchantId, onClose, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // 'freeze' | 'unfreeze' | 'adjust' | 'overdraft'
  const [actionInput, setActionInput] = useState({ reason: '', amount: '', overdraft: '', targetBucket: 'AVAILABLE' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/treasury/merchants/${merchantId}/wallet`);
      setData(r);
      setActionInput(s => ({ ...s, overdraft: r.merchant.overdraftLimitOverride ?? '' }));
    } catch (e) {
      console.error('[Panel] load error:', e);
      alert(e.message);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  const submitAction = async () => {
    try {
      if (actionModal === 'freeze') {
        if (!actionInput.reason || actionInput.reason.trim().length < 3) {
          alert('Reason required (min 3 chars).');
          return;
        }
        await api.post(`/admin/treasury/merchants/${merchantId}/freeze`, { reason: actionInput.reason.trim() });
      } else if (actionModal === 'unfreeze') {
        await api.post(`/admin/treasury/merchants/${merchantId}/unfreeze`);
      } else if (actionModal === 'adjust') {
        const amt = parseFloat(actionInput.amount);
        if (!isFinite(amt) || amt === 0) {
          alert('Enter a non-zero amount (negative for debit).');
          return;
        }
        if (!actionInput.reason || actionInput.reason.trim().length < 3) {
          alert('Reason required (min 3 chars).');
          return;
        }
        await api.post(`/admin/treasury/merchants/${merchantId}/adjust`, {
          amount: amt,
          reason: actionInput.reason.trim(),
          targetBucket: actionInput.targetBucket || 'AVAILABLE'
        });
      } else if (actionModal === 'overdraft') {
        const raw = actionInput.overdraft;
        const value = raw === '' || raw === null ? null : parseFloat(raw);
        if (value !== null && (!isFinite(value) || value < 0)) {
          alert('Enter a non-negative number or leave blank for tier default.');
          return;
        }
        await api.put(`/admin/treasury/merchants/${merchantId}/overdraft`, { overdraftLimitOverride: value });
      }
      setActionModal(null);
      setActionInput({ reason: '', amount: '', overdraft: '', targetBucket: 'AVAILABLE' });
      await load();
      onChange?.();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  return (
    <div className="tr-panel-overlay" onClick={onClose}>
      <div className="tr-panel" onClick={(e) => e.stopPropagation()}>
        <div className="tr-panel-header">
          <div>
            <h2>{data?.merchant?.store?.name || data?.merchant?.username || 'Merchant'}</h2>
            <p>{data?.merchant?.email}</p>
          </div>
          <button className="tr-panel-close" onClick={onClose}><X size={18} /></button>
        </div>

        {loading && <div className="tr-empty">Loading…</div>}

        {!loading && data && (
          <>
            <div className="tr-panel-balances">
              <div className="tr-bal-row">
                <span>Available</span>
                <strong className="tr-amt-pos">{fmtNGN(data.wallet.availableBalance)}</strong>
              </div>
              <div className="tr-bal-row">
                <span>On hold</span>
                <strong>{fmtNGN(data.wallet.onHoldBalance)}</strong>
              </div>
              <div className="tr-bal-row">
                <span>Pending withdrawal</span>
                <strong>{fmtNGN(data.wallet.pendingWithdrawalBalance)}</strong>
              </div>
              <div className="tr-bal-row tr-bal-total">
                <span>Total balance</span>
                <strong>{fmtNGN(data.wallet.balance)}</strong>
              </div>
              <div className="tr-bal-row">
                <span>Overdraft cap</span>
                <strong>{fmtNGN(data.wallet.overdraftLimit)}</strong>
              </div>
              <div className="tr-bal-row">
                <span>Wallet status</span>
                <StatusBadge status={data.wallet.status} />
              </div>
            </div>

            <div className="tr-panel-actions">
              {data.wallet.isFrozen ? (
                <button className="tr-btn-primary" onClick={() => setActionModal('unfreeze')}>
                  <Unlock size={14} /> Unfreeze wallet
                </button>
              ) : (
                <button className="tr-btn-danger" onClick={() => setActionModal('freeze')}>
                  <Lock size={14} /> Freeze wallet
                </button>
              )}
              <button className="tr-btn-secondary" onClick={() => setActionModal('adjust')}>
                <Plus size={14} /> Adjust balance
              </button>
              <button className="tr-btn-secondary" onClick={() => setActionModal('overdraft')}>
                <Sliders size={14} /> Overdraft override
              </button>
            </div>

            <div className="tr-panel-section">
              <h4>KYC</h4>
              <div className="tr-kyc-grid">
                <div><span>Status</span><strong>{data.merchant.merchantKYC?.status || 'NOT_STARTED'}</strong></div>
                <div><span>Level</span><strong>{data.merchant.merchantKYC?.level || 'LEVEL_0'}</strong></div>
                <div><span>Bank verified</span><strong>{data.merchant.merchantKYC?.accountVerified ? 'Yes' : 'No'}</strong></div>
                <div><span>Daily cap</span><strong>{fmtNGN(data.merchant.merchantKYC?.dailyTransactionLimit)}</strong></div>
                <div><span>Monthly cap</span><strong>{fmtNGN(data.merchant.merchantKYC?.monthlyTransactionLimit)}</strong></div>
                <div><span>Tier</span><strong>{data.merchant.merchantTier}</strong></div>
              </div>
            </div>

            {data.openChargebacks.length > 0 && (
              <div className="tr-panel-section">
                <h4>Open chargebacks ({data.openChargebacks.length})</h4>
                {data.openChargebacks.map(c => (
                  <div key={c.id} className="tr-panel-row">
                    <div>
                      <div className="tr-mono-sm">{c.chargebackRef}</div>
                      <div className="tr-panel-row-sub">{c.reason || '—'}</div>
                    </div>
                    <strong className="tr-amt-neg">−{fmtNGN(c.amount)}</strong>
                  </div>
                ))}
              </div>
            )}

            <div className="tr-panel-section">
              <h4>Recent transactions</h4>
              {data.recentTxns.length === 0 && <div className="tr-empty-small">No transactions yet.</div>}
              {data.recentTxns.map(t => (
                <div key={t.id} className="tr-panel-row">
                  <div>
                    <div>{t.category.replace(/_/g, ' ')}</div>
                    <div className="tr-panel-row-sub">{fmtDateTime(t.createdAt)} · {t.status}</div>
                  </div>
                  <strong className={t.type === 'DEBIT' ? 'tr-amt-neg' : 'tr-amt-pos'}>
                    {t.type === 'DEBIT' ? '−' : '+'}{fmtNGN(t.amount)}
                  </strong>
                </div>
              ))}
            </div>

            <div className="tr-panel-section">
              <h4>Recent withdrawals</h4>
              {data.recentWithdrawals.length === 0 && <div className="tr-empty-small">No withdrawals yet.</div>}
              {data.recentWithdrawals.map(w => (
                <div key={w.id} className="tr-panel-row">
                  <div>
                    <div className="tr-mono-sm">{w.withdrawalRef}</div>
                    <div className="tr-panel-row-sub">{w.bankName} {maskAccount(w.accountNumber)} · {w.status}</div>
                  </div>
                  <strong>{fmtNGN(w.amount)}</strong>
                </div>
              ))}
            </div>
          </>
        )}

        {actionModal && (
          <Modal title={actionModalTitle(actionModal)} onClose={() => { setActionModal(null); setActionInput({ reason: '', amount: '', overdraft: '', targetBucket: 'AVAILABLE' }); }}>
            {actionModal === 'freeze' && (
              <>
                <p className="tr-modal-note">The merchant will be unable to withdraw until you unfreeze. They'll receive an email immediately.</p>
                <label className="tr-field">
                  <span>Reason</span>
                  <textarea rows={3} value={actionInput.reason} onChange={(e) => setActionInput(s => ({ ...s, reason: e.target.value }))} />
                </label>
              </>
            )}
            {actionModal === 'unfreeze' && (
              <p className="tr-modal-note">The merchant will be notified that their wallet is active again.</p>
            )}
            {actionModal === 'adjust' && (
              <>
                <p className="tr-modal-note">
                  Manual credit or debit. Negative amount = debit. Choose the wallet bucket to adjust so stale holds can be cleaned up without pushing available balance negative.
                </p>
                <label className="tr-field">
                  <span>Target bucket</span>
                  <select
                    value={actionInput.targetBucket}
                    onChange={(e) => setActionInput(s => ({ ...s, targetBucket: e.target.value }))}
                  >
                    <option value="AVAILABLE">Available balance</option>
                    <option value="ON_HOLD">On hold</option>
                    <option value="PENDING_WITHDRAWAL">Pending withdrawal</option>
                  </select>
                </label>
                <label className="tr-field">
                  <span>Amount (₦, negative for debit)</span>
                  <input type="number" placeholder="e.g. 5000 or -2500" value={actionInput.amount}
                    onChange={(e) => setActionInput(s => ({ ...s, amount: e.target.value }))} />
                </label>
                <label className="tr-field">
                  <span>Reason</span>
                  <textarea rows={3} value={actionInput.reason} onChange={(e) => setActionInput(s => ({ ...s, reason: e.target.value }))} />
                </label>
              </>
            )}
            {actionModal === 'overdraft' && (
              <>
                <p className="tr-modal-note">
                  Override this merchant's overdraft floor. Leave blank to fall back to their tier default ({fmtNGN(data?.wallet?.overdraftLimit)}).
                </p>
                <label className="tr-field">
                  <span>Override (₦, blank = tier default)</span>
                  <input type="number" min="0" placeholder="blank for default" value={actionInput.overdraft}
                    onChange={(e) => setActionInput(s => ({ ...s, overdraft: e.target.value }))} />
                </label>
              </>
            )}
            <div className="tr-modal-actions">
              <button className="tr-btn-secondary" onClick={() => { setActionModal(null); setActionInput({ reason: '', amount: '', overdraft: '' }); }}>Cancel</button>
              <button className="tr-btn-primary" onClick={submitAction}>Confirm</button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

function actionModalTitle(action) {
  switch (action) {
    case 'freeze': return 'Freeze wallet';
    case 'unfreeze': return 'Unfreeze wallet';
    case 'adjust': return 'Adjust wallet balance';
    case 'overdraft': return 'Set overdraft override';
    default: return 'Action';
  }
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
const TypeBadge = ({ type }) => (
  <span className={`tr-type-badge tr-type-${type.toLowerCase()}`}>
    {type === 'CREDIT' ? <ArrowDownLeft size={11} /> : <ArrowUpRight size={11} />}
    {type}
  </span>
);

const StatusBadge = ({ status }) => {
  const tone = ({
    PENDING: 'pending',
    PROCESSING: 'pending',
    QUEUED: 'pending',
    ON_HOLD: 'pending',
    COMPLETED: 'success',
    SUCCESS: 'success',
    WON: 'success',
    RESOLVED: 'success',
    ACTIVE: 'success',
    FAILED: 'danger',
    LOST: 'danger',
    FROZEN: 'danger',
    SUSPENDED: 'danger',
    REVERSED: 'neutral',
    CLOSED: 'neutral'
  })[status] || 'neutral';
  return <span className={`tr-status tr-status-${tone}`}>{status}</span>;
};

const FilterField = ({ label, children }) => (
  <label className="tr-filter-field">
    <span>{label}</span>
    {children}
  </label>
);

const Pager = ({ pagination, onChange }) => (
  <div className="tr-pager">
    <button disabled={pagination.page <= 1} onClick={() => onChange(pagination.page - 1)}>← Prev</button>
    <span>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>
    <button disabled={pagination.page >= pagination.totalPages} onClick={() => onChange(pagination.page + 1)}>Next →</button>
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div className="tr-modal-overlay" onClick={onClose}>
    <div className="tr-modal" onClick={(e) => e.stopPropagation()}>
      <div className="tr-modal-header">
        <h3>{title}</h3>
        <button onClick={onClose}><X size={16} /></button>
      </div>
      <div className="tr-modal-body">{children}</div>
    </div>
  </div>
);

export default TreasuryPage;
